import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { register as registerUser, API_URL } from '../api/api';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { showToast } from '../Layout';
import { Facebook, Globe, Eye, EyeOff, User, Phone, Mail, Lock, CheckCircle, ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import logoWithSlogan from '../imgs/3_trans.png';
import ImageWithFallback from '../components/common/ImageWithFallback';
import SEO from '../components/common/SEO';
import { motion, AnimatePresence } from 'framer-motion';
import TermsModal from '../components/common/TermsModal';
import ReCAPTCHA from 'react-google-recaptcha';

export default function Register() {
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);

  const [showPassword, setShowPassword] = useState(false);
  const password = watch('password');

  const registerMutation = useMutation({
    mutationFn: (userData) => registerUser(userData),
    onSuccess: (data) => {
      localStorage.setItem('swapx_user', JSON.stringify(data));
      showToast(t('registerSuccess'), 'success');
      navigate('/profile');
      window.location.reload();
    },
    onError: (error) => {
      showToast(t(error.message) || t('registrationFailed'), 'error');
    },
  });

  const onSubmit = (data) => {
    if (!captchaToken) {
      showToast(t('pleaseCompleteCaptcha', 'Please complete the CAPTCHA'), 'error');
      return;
    }
    registerMutation.mutate({ ...data, captchaToken });
  };

  const handleGoogleLogin = () => {
      window.location.href = `${API_URL}/auth/google`;
  };

  const handleFacebookLogin = () => {
      window.location.href = `${API_URL}/auth/facebook`;
  };

  const nextStep = () => setStep(2);
  const prevStep = () => setStep(1);

  return (
    <div className="min-h-[90vh] flex items-center justify-center p-4">
      <SEO title={t('register')} description={t('registerAccount')} />
      
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-xl w-full bg-card rounded-[3rem] shadow-2xl border border-border overflow-hidden relative"
      >
        {/* Animated Background Glow */}
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-emerald-500 via-primary to-purple-500 opacity-50" />

        <div className="p-8 md:p-12">
            <div className="flex flex-col items-center mb-10">
                <Link to="/" className="mb-6 hover:scale-105 transition-transform duration-500">
                    <ImageWithFallback src={logoWithSlogan} alt="Ahlafot" className="h-20 md:h-24 w-auto object-contain" />
                </Link>
                <h2 className="text-3xl font-black uppercase tracking-tighter text-center">
                    {t('registerAccount')}
                </h2>
                
                {/* Step Indicator */}
                <div className="flex items-center gap-3 mt-6">
                    <div className={`h-1.5 rounded-full transition-all duration-500 ${step === 1 ? 'w-12 bg-primary' : 'w-6 bg-muted'}`} />
                    <div className={`h-1.5 rounded-full transition-all duration-500 ${step === 2 ? 'w-12 bg-primary' : 'w-6 bg-muted'}`} />
                </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
                <AnimatePresence mode="wait">
                    {step === 1 ? (
                        <motion.div 
                            key="step1"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            className="space-y-5"
                        >
                            <div className="space-y-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">{t('fullName')}</label>
                                <div className="relative group">
                                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors w-5 h-5" />
                                    <input
                                        type="text"
                                        required
                                        {...register('full_name', { required: t('fullNameRequired') })}
                                        className="w-full bg-muted/50 border-none rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-primary/20 font-bold text-sm"
                                        placeholder={t('fullNamePlaceholder')}
                                    />
                                </div>
                                {errors.full_name && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 uppercase">{errors.full_name.message}</p>}
                            </div>

                            <div className="space-y-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">{t('emailAddress')}</label>
                                <div className="relative group">
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors w-5 h-5" />
                                    <input
                                        type="email"
                                        required
                                        {...register('email', { required: t('emailRequired') })}
                                        className="w-full bg-muted/50 border-none rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-primary/20 font-bold text-sm"
                                        placeholder={t('emailAddressPlaceholder')}
                                    />
                                </div>
                                {errors.email && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 uppercase">{errors.email.message}</p>}
                            </div>

                            <div className="space-y-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">{t('phoneNumber')}</label>
                                <div className="relative group">
                                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors w-5 h-5" />
                                    <input
                                        type="tel"
                                        required
                                        {...register('phone', { required: t('phoneNumberRequired') })}
                                        className="w-full bg-muted/50 border-none rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-primary/20 font-bold text-sm"
                                        placeholder={t('phoneNumberPlaceholder')}
                                    />
                                </div>
                                {errors.phone && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 uppercase">{errors.phone.message}</p>}
                            </div>

                            <button
                                type="button"
                                onClick={nextStep}
                                className="w-full bg-primary text-white py-5 rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3 mt-8"
                            >
                                {t('next', 'Continue to Security')}
                                <ArrowRight size={18} />
                            </button>
                        </motion.div>
                    ) : (
                        <motion.div 
                            key="step2"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-5"
                        >
                            <div className="space-y-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">{t('password')}</label>
                                <div className="relative group">
                                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors w-5 h-5" />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        required
                                        {...register('password', { 
                                            required: t('passwordRequired'), 
                                            minLength: { value: 6, message: t('passwordMinLength') } 
                                        })}
                                        className="w-full bg-muted/50 border-none rounded-2xl py-4 pl-12 pr-12 focus:ring-2 focus:ring-primary/20 font-bold text-sm"
                                        placeholder="••••••••"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                                    >
                                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                                {errors.password && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 uppercase">{errors.password.message}</p>}
                            </div>

                            <div className="space-y-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">{t('confirmPassword')}</label>
                                <div className="relative group">
                                    <CheckCircle className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors w-5 h-5" />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        required
                                        {...register('confirm_password', {
                                            required: t('confirmPasswordRequired'),
                                            validate: value => value === password || t('passwordsMismatch')
                                        })}
                                        className="w-full bg-muted/50 border-none rounded-2xl py-4 pl-12 pr-12 focus:ring-2 focus:ring-primary/20 font-bold text-sm"
                                        placeholder="••••••••"
                                    />
                                </div>
                                {errors.confirm_password && <p className="text-red-500 text-[10px] font-bold mt-1 ml-1 uppercase">{errors.confirm_password.message}</p>}
                            </div>

                            <div className="flex items-center gap-3 p-4 bg-primary/5 rounded-2xl border border-primary/10 mt-2">
                                <input 
                                    type="checkbox" 
                                    id="terms"
                                    {...register('terms', { required: true })}
                                    className="w-5 h-5 rounded border-primary/20 text-primary focus:ring-primary/20 cursor-pointer"
                                />
                                <label htmlFor="terms" className="text-[10px] font-bold leading-tight cursor-pointer">
                                    {t('agreeToTerms', 'I agree to the')} 
                                    <button 
                                        type="button" 
                                        onClick={() => setIsTermsModalOpen(true)}
                                        className="text-primary hover:underline ml-1 font-black"
                                    >
                                        {t('termsAndConditions', 'Terms & Conditions')}
                                    </button>
                                </label>
                            </div>

                            <div className="flex justify-center mt-4 mb-2">
                                <ReCAPTCHA
                                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'} // fallback to google test key for dev
                                    onChange={(token) => setCaptchaToken(token)}
                                    theme="light"
                                />
                            </div>

                            <div className="flex gap-3 mt-4">
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className="flex-1 bg-muted/50 text-muted-foreground py-5 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-muted transition-all active:scale-95"
                                >
                                    {t('back')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={registerMutation.isLoading}
                                    className="flex-[2] bg-primary text-white py-5 rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                                >
                                    {registerMutation.isLoading ? <Loader2 className="animate-spin" /> : (
                                        <>
                                            {t('register')}
                                            <CheckCircle size={18} />
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="relative my-10">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-border"></div>
                    </div>
                    <div className="relative flex justify-center text-[10px] uppercase font-black tracking-widest">
                        <span className="bg-card px-4 text-muted-foreground opacity-50">{t('orContinueWith')}</span>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <button
                        type="button"
                        onClick={handleGoogleLogin}
                        className="flex items-center justify-center gap-2 py-4 bg-muted/30 rounded-2xl hover:bg-muted transition-all font-black text-[10px] uppercase tracking-widest active:scale-95 border border-transparent hover:border-border"
                    >
                        <Globe size={18} className="text-primary" />
                        {t('google')}
                    </button>
                    <button
                        type="button"
                        onClick={handleFacebookLogin}
                        className="flex items-center justify-center gap-2 py-4 bg-muted/30 rounded-2xl hover:bg-muted transition-all font-black text-[10px] uppercase tracking-widest active:scale-95 border border-transparent hover:border-border"
                    >
                        <Facebook size={18} className="text-[#1877F2]" />
                        {t('facebook')}
                    </button>
                </div>
            </form>

            <div className="mt-10 text-center">
                <p className="text-xs font-bold text-muted-foreground mb-2">{t('haveAccount')}</p>
                <Link to="/login" className="text-primary font-black uppercase tracking-[0.15em] text-xs hover:underline hover:scale-105 inline-block transition-transform">
                    {t('signIn')}
                </Link>
            </div>
        </div>
      </motion.div>

      <TermsModal 
        isOpen={isTermsModalOpen} 
        onClose={() => setIsTermsModalOpen(false)} 
      />
    </div>
  );
}
